import React, { useEffect, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import RichTextOption from '../../../../../organisms/editor-design/options/RichTextOption';
import { registerQuillStyleAttributors } from '../content/fields/quillAttributors';

registerQuillStyleAttributors();

// Transparent so the editor inherits the hero's own typography (font, size,
// line-height, weight, color, alignment) — what you see while editing is
// pixel-identical to the published render, which is what removes the spacing
// jump between edit and preview.
const TRANSPARENT_EDITOR = { minHeight: 0, background: 'transparent' };

// Captures the starting HTML ONCE (useState initializer) so the editor is
// uncontrolled after mount. Feeding a value that changes on every keystroke
// makes Quill re-paste and lose the caret / swallow Enter — this avoids that.
const Editor = ({ initialHtml, onChange, toolbar }) => {
  const [initial] = useState(initialHtml);
  return (
    <RichTextOption
      optionId="inline"
      defaultValue={initial}
      onChange={onChange}
      toolbarOptions={toolbar}
      editorStyle={TRANSPARENT_EDITOR}
    />
  );
};

// Click-to-edit rich text used for the path hero headline + sub-header. Resting
// state renders the sanitized HTML with the caller's class so surrounding CSS
// gives the published size/weight/color; clicking (when editable) swaps to an
// in-place Quill editor that inherits that same styling, with a compact floating
// toolbar (styled in PathEditorV2.scss). Clicking away exits edit mode.
const InlineRichText = ({ value, onSave, editable = false, className = '', placeholder = 'Click to edit…', toolbar }) => {
  const [editing, setEditing] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!editing) return undefined;
    const onDocMouseDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setEditing(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [editing]);

  if (editable && editing) {
    return (
      <div
        className={`${className} un-inline-rte`}
        ref={wrapRef}
        onClick={(e) => e.stopPropagation()}
      >
        <Editor initialHtml={value || ''} onChange={onSave} toolbar={toolbar} />
      </div>
    );
  }

  const raw = value || '';
  const hasContent = raw.replace(/<[^>]*>/g, '').trim().length > 0 || /<img|<br/i.test(raw);
  const html = DOMPurify.sanitize(hasContent ? raw : '');

  return (
    <div
      className={className}
      role={editable ? 'textbox' : undefined}
      tabIndex={editable ? 0 : undefined}
      onClick={editable ? () => setEditing(true) : undefined}
      dangerouslySetInnerHTML={hasContent ? { __html: html } : undefined}
    >
      {hasContent ? undefined : (editable ? <span className="un-inline-rte__ph">{placeholder}</span> : null)}
    </div>
  );
};

export default InlineRichText;
